import type { Vec2, Vec3 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { type ShipTemplate, type ShipType } from './shipTemplates';
import { hullStations, starboardSection, type HullStation } from './hull';
import { loftSolid, type Ring3 } from './shipSolids';
import { girthFractions, girthLength, plankingStyle } from './shipPatterns';
import { hullStylesOf, type HullPalette, type ShipTier } from './hullParts';
import {
  buildBoatJoinery,
  buildFloorRibs,
  buildFrames,
  buildLighterBeams,
  buildLighterKeelson,
  buildOars,
  buildThwarts,
} from './boatFittings';
import { innerStarboard } from './boatSection';

export { OPEN_BOAT_SHELL, floorHeight } from './boatSection';

/*
 * The painting draws the Damrak's lighters open, a pale gunwale round a dark hold crossed by
 * transverse strokes (scan 3570,2740-3740,2830); the woodcut's rowing boat is open, its rowers
 * seated on thwarts, its oars out (woodcut 3140,3260-3330,3380), and its small craft planked
 * clinker along the sheer (2640,2900-2820,3100). Seen from the quay, a boat is drawn so in the
 * mid and near tiers: an open shell, planked outside, its gunwale capped, its hold in shade, with
 * rowing benches and shipped oars on the smaller rowing craft. Long lighters instead have
 * open central holds, narrow end ties and repeated ribs. Beyond 250 m and in the painter view,
 * these transverse strokes survive with the dark hold and lighter gunwale.
 */

const MASK_FRAME: Frame = { origin: [0, 0, 0], yaw: 0 };
const OPEN_BOATS: ReadonlySet<ShipType> = new Set<ShipType>(['rowing', 'praam']);

export function openBoatTypes(): ReadonlySet<ShipType> {
  return OPEN_BOATS;
}

export function isOpenBoat(template: ShipTemplate): boolean {
  return OPEN_BOATS.has(template.type);
}

const mirrored = (points: readonly Vec3[]): Vec3[] => points.map(([x, y, z]): Vec3 => [x, y, -z]);

/* Keel, the outside up the starboard side, across the gunwale cap, the inside down to the floor
   and its middle, up the inside of the port side, across its cap, and down its outside: the
   sweep crossed with the tangent points out of the shell's wood everywhere. At the stem and the
   sternpost the inside closes on the post's head, so the hold rises to it and never crosses the
   planking where the two meet. */
function openRing(st: HullStation): Ring3 {
  const [keel, ...outside] = starboardSection(st);
  const head: Vec3 = [st.x, st.gunwale, 0];
  const post = st.halfBreadth <= 0;
  const inside = post ? outside.map(() => head) : innerStarboard(st);
  const middle: Vec3 = post ? head : [st.x, inside.at(-1)![1], 0];
  const port = [...mirrored(inside).reverse(), ...mirrored(outside).reverse()];
  return [keel!, ...outside, ...inside, middle, ...port];
}

/* Ring point k's place on the outside girth, keel 0 to gunwale 1; the inside points take none. */
function outsidePoint(k: number, rows: number): number {
  return k <= rows ? k : 2 * (2 * rows + 1) - k;
}

function outsideUv(stations: readonly HullStation[], length: number, rows: number) {
  const fractions = stations.map((st) => girthFractions(starboardSection(st)));
  return (r: number, k: number): Vec2 => [
    stations[r]!.x + length / 2,
    fractions[r]![outsidePoint(k, rows)] ?? 0,
  ];
}

/* Which surface ring strip k is: the planked outside, the gunwale caps or the hold. */
function stripStyles(rows: number, planking: VertexStyle, cap: VertexStyle, hold: VertexStyle) {
  const ringLength = 2 * (2 * rows + 1);
  return (k: number): VertexStyle => {
    if (k < rows || k >= ringLength - rows) return planking;
    if (k === rows || k === ringLength - rows - 1) return cap;
    return hold;
  };
}

function buildShell(
  builder: MeshBuilder,
  template: ShipTemplate,
  styles: ReturnType<typeof hullStylesOf>
): void {
  const stations = hullStations(template);
  const midship = starboardSection(stations[stations.length >> 1]!);
  const rows = midship.length - 1;
  const planking = plankingStyle(styles.hull, template.type, girthLength(midship));
  loftSolid(
    builder,
    styles.hull,
    stations.map(openRing),
    stripStyles(rows, planking, styles.wale, styles.port),
    outsideUv(stations, template.length, rows)
  );
}

/**
 * An open boat's body for one distance tier (bow at -X): rowboats retain thwarts and oars,
 * while lighters retain narrow ties and a ribbed open hold. Cleats appear in the mid tier;
 * full frames and a lighter's central timber appear near. Every part is a closed solid
 * facing outward. Distant lighters keep the painted transverse strokes, not a flat lid.
 */
export function buildBoatBody(
  builder: MeshBuilder,
  template: ShipTemplate,
  tier: ShipTier,
  palette: HullPalette,
  edgeId: number,
  seed: number
): void {
  if (!isOpenBoat(template)) throw new Error(`${template.type} is not an open boat`);
  const styles = hullStylesOf(palette, edgeId, seed);
  buildShell(builder, template, styles);
  const lighter = template.type === 'praam';
  if (lighter) {
    buildLighterBeams(builder, template, styles.wale);
    if (tier !== 'near') buildFloorRibs(builder, template, styles.wale);
  } else {
    buildThwarts(builder, template, styles.wale);
  }
  if (tier === 'far') return;
  if (!lighter) buildOars(builder, template, styles.wale);
  buildBoatJoinery(builder, template, styles.wale);
  if (tier === 'near') {
    buildFrames(builder, template, lighter ? styles.wale : styles.hull);
    if (lighter) buildLighterKeelson(builder, template, styles.hull);
  }
}

/** An open boat as the yard has it on its blocks: its shell and frames, no thwarts or oars yet. */
export function buildBoatShell(
  builder: MeshBuilder,
  template: ShipTemplate,
  palette: HullPalette,
  edgeId: number,
  seed: number
): void {
  if (!isOpenBoat(template)) throw new Error(`${template.type} is not an open boat`);
  const styles = hullStylesOf(palette, edgeId, seed);
  buildShell(builder, template, styles);
  buildFrames(builder, template, styles.hull);
}

/** How far under the gunwale's inner edge the hold's mask lies, clear of the cap's own face. */
const HOLD_MASK_DROP_M = 0.02;

/**
 * The mouth of an open boat's hold as one sheet between the inner edges of its gunwales (bow at
 * -X), facing up. The water is a level plane that passes through a floating hull, so it would
 * show inside the hold and the boat would look swamped. Drawn into the depth buffer alone, after
 * the hull and before the water, this sheet keeps the water out of every view into the hold.
 */
export function buildHoldMask(
  builder: MeshBuilder,
  template: ShipTemplate,
  style: VertexStyle
): void {
  if (!isOpenBoat(template)) throw new Error(`${template.type} is not an open boat`);
  const edges = hullStations(template).map((st): Vec3 => {
    const [x, y, z] = innerStarboard(st)[0]!;
    return [x, y - HOLD_MASK_DROP_M, st.halfBreadth <= 0 ? 0 : z];
  });
  for (let i = 1; i < edges.length; i++) {
    const [a, b] = [edges[i - 1]!, edges[i]!];
    if (a[2] <= 0 && b[2] <= 0) continue;
    // At the stem and the sternpost the two edges meet, and the sheet ends in a point.
    const sides = (p: Vec3): Vec3[] => (p[2] > 0 ? [p, [p[0], p[1], -p[2]]] : [p]);
    builder.polygon(style, MASK_FRAME, [...sides(a).reverse(), ...sides(b)]);
  }
}
